import { API, apiUrl } from '@/config';
import { ApiError, refreshOnce } from './client';
import { getAccessToken } from './tokens';

/**
 * A list request that keeps the pagination envelope.
 *
 * The shared client unwraps `{ data }` for every caller, which is right for the
 * ninety percent of calls that want the payload and nothing else - and wrong
 * for a table, which needs `meta` to know whether another page exists.
 *
 * The admin portal solved this by hand-rolling a fetch inside its one list
 * service. This surface has five paginated tables, so the workaround is
 * extracted here instead of copied five times. Changing the shared client to
 * return the whole envelope was the alternative, and it would push that shape
 * onto every other call site for the sake of these.
 *
 * The 401-then-refresh behaviour is reproduced deliberately rather than skipped:
 * a table left out of it would be the one place in the portal where an expired
 * token logs somebody out instead of quietly renewing.
 */
export interface Paginated<T> {
  data: T[];
  meta: { total: number; limit: number; offset: number; hasMore: boolean };
}

/** Drops undefined, empty and null values so they never reach the query string. */
export function toQueryString(query: Record<string, unknown>): string {
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }

  return params.size > 0 ? `?${params.toString()}` : '';
}

async function attempt(path: string, signal: AbortSignal | undefined): Promise<Response | null> {
  const token = getAccessToken();

  return fetch(apiUrl(path), {
    headers: token ? { authorization: `Bearer ${token}` } : {},
    credentials: 'include',
    signal: signal ?? AbortSignal.timeout(API.timeoutMs),
  }).catch(() => null);
}

export async function getPaginated<T>(
  path: string,
  query: Record<string, unknown> = {},
  signal?: AbortSignal,
  fallbackMessage = 'Could not load that list.',
): Promise<Paginated<T>> {
  const url = `${path}${toQueryString(query)}`;

  let response = await attempt(url, signal);

  /*
   * One retry, and only for 401. `refreshOnce` is shared with the main client,
   * so several tables expiring at the same moment produce one refresh between
   * them - the server treats a second exchange of the same refresh token as
   * theft and would revoke the whole session family.
   */
  if (response?.status === 401 && (await refreshOnce())) {
    response = await attempt(url, signal);
  }

  if (!response) throw new ApiError(0, null, 'Could not reach the server.');
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as null;
    throw new ApiError(response.status, body, fallbackMessage);
  }

  return (await response.json()) as Paginated<T>;
}
